import React from 'react';

import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

export const AGENT_AVATAR_CLIP_PATH = `polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)`;
const AGENT_AVATAR_BORDER_WIDTH = 2;
const AGENT_AVATAR_SIZE_PX = {
	small: 24,
	medium: 32,
};

const rootStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	position: 'relative',
});

const borderStyles = css({
	display: 'flex',
	flexShrink: 0,
	alignItems: 'center',
	justifyContent: 'center',
});

const borderClipStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	clipPath: AGENT_AVATAR_CLIP_PATH,
});

const badgeStyle: React.CSSProperties = {
	backgroundColor: 'var(--agent-presence-badge-background)',
	bottom: '-2px',
	color: 'var(--agent-presence-badge-color)',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: 10,
	right: '-2px',
	userSelect: 'none',
	zIndex: 2,
};

const getBorderStyle = (size: 'small' | 'medium'): React.CSSProperties => {
	const borderedAvatarSize = AGENT_AVATAR_SIZE_PX[size] + AGENT_AVATAR_BORDER_WIDTH * 2;

	return {
		backgroundColor: `var(--agent-presence-border-color, ${token('color.icon.inverse')})`,
		height: borderedAvatarSize,
		width: borderedAvatarSize,
	};
};

const badgeStyles = css({
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '12px',
	width: '12px',
	borderRadius: token('radius.small'),
	fontWeight: token('font.weight.semibold'),
});

export type AgentAvatarFrameProps = {
	avatar: React.ReactNode;
	badgeLabel?: string;
	badgeTestId?: string;
	isOverlapped?: boolean;
	stackIndex?: number;
	showBorder?: boolean;
	showPresenceBadge?: boolean;
	size?: 'small' | 'medium';
	testId?: string;
};

export const AgentAvatarFrame = ({
	avatar,
	badgeLabel,
	badgeTestId = 'agent-presence-avatar-badge',
	isOverlapped,
	stackIndex,
	showBorder,
	showPresenceBadge,
	size = 'medium',
	testId = 'agent-presence-avatar',
}: AgentAvatarFrameProps): React.JSX.Element => {
	const rootStyle: React.CSSProperties = {
		marginInlineStart: isOverlapped ? token('space.negative.075') : undefined,
		zIndex: stackIndex,
	};
	const borderStyle = getBorderStyle(size);

	return (
		<span
			css={rootStyles}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime z-index and avatar presence colors.
			style={rootStyle}
			data-testid={testId}
		>
			{showBorder ? (
				<span css={borderClipStyles}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime dimensions to match the selected avatar size plus the presence border. */}
					<span css={borderStyles} style={borderStyle}>
						{avatar}
					</span>
				</span>
			) : (
				avatar
			)}
			{showPresenceBadge &&
				badgeLabel && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Uses runtime presence colors and badge offsets that align with the overlapped avatar.
					<span css={badgeStyles} style={badgeStyle} data-testid={badgeTestId}>
						{badgeLabel}
					</span>
				)}
		</span>
	);
};
